/**
 * DataValidation — AI-powered data quality report showing anomalies
 * across employees, assets, sites, and device health.
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { StatusBadge, type BadgeVariant } from '@/components/ui/StatusBadge';

interface Anomaly {
  category: string;
  severity: string;
  description: string;
  count: number;
  details: Record<string, unknown>[];
}

interface ValidationReport {
  totalAnomalies: number;
  categories: number;
  anomalies: Anomaly[];
  generatedAt: string;
}

const SEVERITY_VARIANTS: Record<string, BadgeVariant> = {
  High: 'red',
  Medium: 'yellow',
  Low: 'gray',
};

async function fetchValidation(): Promise<ValidationReport> {
  const res = await fetch('/api/employees/validation', { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to load validation report');
  return res.json();
}

export function DataValidation() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['data-validation'],
    queryFn: fetchValidation,
  });

  if (isLoading) return <LoadingSpinner message="Running data validation checks…" />;
  if (error) return <ErrorMessage message={(error as Error).message} onRetry={() => refetch()} />;
  if (!data) return null;

  function exportAnomalyCSV(anomaly: Anomaly) {
    if (anomaly.details.length === 0) return;
    const headers = Object.keys(anomaly.details[0]);
    const rows = anomaly.details.map(row =>
      headers.map(h => {
        const val = row[h];
        const str = val == null ? '' : String(val);
        return `"${str.replace(/"/g, '""')}"`;
      }).join(',')
    );
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${anomaly.category.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function exportTitleReport(details: Record<string, unknown>[]) {
    const rows: string[] = ['Issue,Variants,Employee Name,Current Title,Email,Site'];
    for (const group of details) {
      const issue = String(group.issue ?? '');
      const variants = (group.variants ?? []) as Array<{ title: string; count: number }>;
      const variantStr = variants.map(v => `${v.title} (${v.count})`).join(' / ');
      const employees = (group.employees ?? []) as Record<string, unknown>[];
      for (const emp of employees) {
        rows.push([
          `"${issue}"`,
          `"${variantStr}"`,
          `"${String(emp.LegalName ?? '')}"`,
          `"${String(emp.JobTitle ?? '')}"`,
          `"${String(emp.WorkEmail ?? '')}"`,
          `"${String(emp.ResolvedSiteCode ?? '')}"`,
        ].join(','));
      }
    }
    const csv = rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `title-inconsistencies-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="max-w-5xl">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-lg font-semibold text-text-primary m-0">Data Validation Report</h1>
          <p className="text-sm text-slate-500 mt-1">
            {data.totalAnomalies} anomalies across {data.categories} categories · Generated {new Date(data.generatedAt).toLocaleString()}
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 rounded-lg border border-border bg-white text-sm font-medium text-slate-600 cursor-pointer hover:bg-page transition-all"
        >
          Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {['High', 'Medium', 'Low'].map((sev) => {
          const count = data.anomalies.filter(a => a.severity === sev).reduce((s, a) => s + a.count, 0);
          return (
            <div key={sev} className="bg-white rounded-lg border border-border p-4 text-center">
              <div className={`text-2xl font-bold ${sev === 'High' ? 'text-red-600' : sev === 'Medium' ? 'text-amber-600' : 'text-slate-600'}`}>
                {count}
              </div>
              <div className="text-xs text-slate-500 mt-1">{sev} Severity</div>
            </div>
          );
        })}
      </div>

      {/* Anomaly sections */}
      {data.anomalies.length === 0 ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-center">
          <div className="text-2xl mb-2">✅</div>
          <p className="text-emerald-700 font-medium">No data anomalies detected</p>
        </div>
      ) : (
        <div className="space-y-4">
          {data.anomalies.map((anomaly, i) => (
            <div key={i} className="bg-white rounded-lg border border-border p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <StatusBadge label={anomaly.severity} variant={SEVERITY_VARIANTS[anomaly.severity] ?? 'gray'} />
                  <h2 className="text-base font-semibold text-text-primary m-0">{anomaly.category}</h2>
                  <span className="text-sm text-slate-500">({anomaly.count})</span>
                </div>
              </div>
              <p className="text-sm text-slate-600 mb-3">{anomaly.description}</p>

              {/* Special rendering for Job Title Inconsistencies */}
              {anomaly.category.includes('Job Title') && anomaly.details.length > 0 ? (
                <div>
                  <div className="flex justify-end mb-2">
                    <button
                      onClick={() => exportTitleReport(anomaly.details)}
                      className="px-3 py-1.5 rounded-lg border border-border bg-white text-xs font-medium text-slate-600 cursor-pointer hover:bg-page transition-all"
                    >
                      Export CSV
                    </button>
                  </div>
                  <div className="space-y-4">
                    {anomaly.details.map((group: Record<string, unknown>, gi: number) => (
                      <div key={gi} className="bg-page rounded-lg p-4 border border-border-subtle">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-sm font-semibold text-text-primary">
                            {String(group.issue ?? '')}
                          </span>
                          <span className="text-xs text-slate-500">({String(group.totalAffected ?? 0)} affected)</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {((group.variants as Array<{ title: string; count: number }>) ?? []).map((v, vi: number) => (
                            <span key={vi} className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                              {v.title} ({v.count})
                            </span>
                          ))}
                        </div>
                        {Array.isArray(group.employees) && (group.employees as Record<string, unknown>[]).length > 0 && (
                          <table className="w-full text-[12px] border-collapse">
                            <thead>
                              <tr className="border-b border-border">
                                <th className="text-left py-1 px-2 font-medium text-slate-500">Employee</th>
                                <th className="text-left py-1 px-2 font-medium text-slate-500">Current Title</th>
                                <th className="text-left py-1 px-2 font-medium text-slate-500">Email</th>
                                <th className="text-left py-1 px-2 font-medium text-slate-500">Site</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(group.employees as Record<string, unknown>[]).map((emp, ei) => (
                                <tr key={ei} className="border-b border-border-subtle">
                                  <td className="py-1 px-2">
                                    {emp.EmployeeId ? (
                                      <Link to={`/employees/${emp.EmployeeId}`} className="text-baker-red no-underline hover:underline font-medium">
                                        {String(emp.LegalName ?? '')}
                                      </Link>
                                    ) : String(emp.LegalName ?? '')}
                                  </td>
                                  <td className="py-1 px-2 text-slate-600">{String(emp.JobTitle ?? '—')}</td>
                                  <td className="py-1 px-2 text-slate-500">{String(emp.WorkEmail ?? '—')}</td>
                                  <td className="py-1 px-2">
                                    {emp.ResolvedSiteCode ? (
                                      <Link to={`/sites/${emp.ResolvedSiteCode}`} className="text-baker-red no-underline hover:underline">
                                        {String(emp.ResolvedSiteCode)}
                                      </Link>
                                    ) : '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : anomaly.details.length > 0 ? (
                <details>
                  <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-600 mb-2">
                    View details ({anomaly.details.length} items)
                  </summary>
                  <div className="flex justify-end mb-2">
                    <button
                      onClick={() => exportAnomalyCSV(anomaly)}
                      className="px-3 py-1.5 rounded-lg border border-border bg-white text-xs font-medium text-slate-600 cursor-pointer hover:bg-page transition-all"
                    >
                      Export CSV
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[12px] border-collapse">
                      <thead>
                        <tr className="border-b border-border">
                          {Object.keys(anomaly.details[0]).map((key) => (
                            <th key={key} className="text-left py-1 px-2 font-medium text-slate-500">{key}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {anomaly.details.slice(0, 50).map((row, idx) => (
                          <tr key={idx} className="border-b border-border-subtle">
                            {Object.entries(row).map(([key, val], cidx) => (
                              <td key={cidx} className="py-1 px-2 text-slate-700">
                                {key === 'AssetId' && typeof val === 'number' ? (
                                  <Link to={`/assets/${val}`} className="text-baker-red no-underline hover:underline font-medium">{val}</Link>
                                ) : key === 'EmployeeId' && typeof val === 'number' ? (
                                  <Link to={`/employees/${val}`} className="text-baker-red no-underline hover:underline font-medium">{val}</Link>
                                ) : key === 'SiteNumber' && typeof val === 'string' && /^\d{3}$/.test(val) ? (
                                  <Link to={`/sites/${val}`} className="text-baker-red no-underline hover:underline">{val}</Link>
                                ) : key === 'AssetSite' && typeof val === 'string' && /^\d{3}$/.test(val) ? (
                                  <Link to={`/sites/${val}`} className="text-baker-red no-underline hover:underline">{val}</Link>
                                ) : key === 'EmployeeSite' && typeof val === 'string' && /^\d{3}$/.test(val) ? (
                                  <Link to={`/sites/${val}`} className="text-baker-red no-underline hover:underline">{val}</Link>
                                ) : key === 'ResolvedSiteCode' && typeof val === 'string' && /^\d{3}$/.test(val) ? (
                                  <Link to={`/sites/${val}`} className="text-baker-red no-underline hover:underline">{val}</Link>
                                ) : val == null ? '—' : String(val).substring(0, 80)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
